import type { ScreenMonitor } from '@viewer/protocol';
import { useEffect, useRef, useState } from 'react';
import { monitorLabel } from './monitors';
import styles from './MonitorPicker.module.css';

interface Props {
  monitors: ScreenMonitor[];
  /** Monitor mostrado na janela principal; `null` mostra todos juntos. */
  shown: number | null;
  /** Monitores abertos em janela separada. */
  detached: number[];
  onShow(index: number | null): void;
  onDetach(index: number): void;
  onReattach(index: number): void;
}

/** Escolha do monitor remoto na janela principal e desacoplamento em outra janela. */
export function MonitorPicker({ monitors, shown, detached, onShow, onDetach, onReattach }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        aria-expanded={open}
        aria-controls="monitor-list"
        onClick={() => setOpen((value) => !value)}
      >
        Monitores
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden="true">
          ›
        </span>
      </button>

      {open && (
        <ul id="monitor-list" className={styles.panel} aria-label="Monitores do computador remoto">
          {monitors.map((monitor, index) => {
            const label = monitorLabel(index, monitor);
            const isDetached = detached.includes(index);
            return (
              <li key={index} className={styles.item}>
                <button
                  type="button"
                  className={`${styles.monitor} ${shown === index ? styles.monitorActive : ''}`}
                  aria-pressed={shown === index}
                  disabled={isDetached}
                  onClick={() => onShow(index)}
                >
                  <span>{label}</span>
                  {isDetached && <span className={styles.state}>Em janela separada</span>}
                </button>
                <button
                  type="button"
                  className={`${styles.icon} ${isDetached ? styles.iconDetached : ''}`}
                  aria-label={isDetached ? `Reanexar o ${label}` : `Abrir o ${label} em uma janela separada`}
                  title={
                    isDetached
                      ? 'Fecha a janela separada e devolve o monitor à janela principal'
                      : 'Abre este monitor em uma janela separada, que você pode mover para outra tela e redimensionar'
                  }
                  onClick={() => (isDetached ? onReattach(index) : onDetach(index))}
                >
                  <span aria-hidden="true">{isDetached ? '↙' : '↗'}</span>
                </button>
              </li>
            );
          })}
          <li className={`${styles.item} ${styles.all}`}>
            <button
              type="button"
              className={`${styles.monitor} ${shown === null ? styles.monitorActive : ''}`}
              aria-pressed={shown === null}
              title="Mostra todos os monitores juntos, como estão no computador remoto"
              onClick={() => onShow(null)}
            >
              Todos juntos
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
